/**
 * Frontend (boshqaruv paneli) — rolga bog'liq ruxsatlar.
 * SaaS: `SUPER_ADMIN` = PLATFORMA superadmini (tenantId=null) — faqat SaaS bo'limlarini
 * ko'radi. Tenant top admini endi `DIRECTOR` (yoki ADMIN). Premium/stikerlar tenant
 * admindan olindi (superadminda). Migratsiyadan so'ng yagona SUPER_ADMIN — platforma
 * superadmini, shu sabab rol bo'yicha aniqlash ishonchli.
 */

export type Section =
  | 'dashboard' | 'students' | 'classes' | 'subjects' | 'schedule'
  | 'attendance' | 'grades' | 'journal' | 'academic-years' | 'homework' | 'payments' | 'reports'
  | 'admissions' | 'contracts' | 'announcements' | 'notifications' | 'stickers' | 'expenses' | 'payroll' | 'staff' | 'settings' | 'support' | 'premium' | 'parents' | 'teachers' | 'banners'
  // Reyting tizimi (ball, xulq mezonlari, test imtihonlar)
  | 'ranking' | 'behavior' | 'exams' | 'faceid'
  // SaaS platforma bo'limlari
  | 'tenants' | 'qurilmalar' | 'agentlar' | 'saas-premium' | 'saas-gifts' | 'saas-support' | 'platform-support' | 'audit';

// Tenant admini (DIRECTOR/ADMIN) bo'limlari — stikersiz, platform-support bilan.
// `premium` — maktab ichidagi Premium sovg'alar tarixi (backend `/premium/*` STAFF = DIRECTOR/ADMIN);
// obuna berish/bekor qilish esa superadminda (`saas-premium`).
const TENANT_ALL: Section[] = [
  'dashboard', 'students', 'parents', 'teachers', 'classes', 'subjects', 'schedule', 'attendance',
  'grades', 'journal', 'academic-years', 'homework', 'ranking', 'behavior', 'exams', 'payments', 'reports', 'expenses', 'payroll', 'admissions', 'contracts', 'announcements',
  'banners', 'notifications', 'premium', 'support', 'staff', 'faceid', 'platform-support', 'settings',
];

// Platforma superadmini bo'limlari (SaaS nazorati).
const PLATFORM: Section[] = ['tenants', 'qurilmalar', 'agentlar', 'saas-premium', 'saas-gifts', 'saas-support', 'audit', 'stickers', 'settings'];

/**
 * `contracts` ATAYLAB `admissions` dan ALOHIDA bo'lim.
 * Avval shartnoma UI'si `admissions` ruxsatiga ilashtirilgan edi: qabulni (masalan)
 * RECEPTION'dan olib qo'yish shartnomalarni ham BEIXTIYOR yopib qo'yardi. Ro'yxat
 * backend bilan mos: `middleware/authorize.MANAGERS` = DIRECTOR, ADMIN, ACCOUNTANT,
 * RECEPTION (+ tenant ichidagi SUPER_ADMIN). Platforma superadminida tenant konteksti
 * yo'q — shu sabab PLATFORM ro'yxatiga kirmaydi.
 */
/* MOLIYA (to'lovlar, hisobotlar, xarajatlar, maosh) — faqat direktor va buxgalter
   (2026-10, maktab egasining qarori). Backend: `authorize.MONEY` (`/payroll/oylik` ham). */
const MONEY_SECTIONS: Section[] = ['payments', 'reports', 'expenses', 'payroll'];

export const ROLE_SECTIONS: Record<string, Section[]> = {
  SUPER_ADMIN: PLATFORM,
  DIRECTOR: TENANT_ALL,
  ADMIN: TENANT_ALL.filter((s) => !MONEY_SECTIONS.includes(s)),
  // `teachers`, `attendance`, `staff` va `faceid` (2026-10): maoshni buxgalter beradi —
  // o'qituvchi va xodimlarni direktor kabi boshqaradi (rol ierarxiyasi bilan), davomat va
  // FACE ID'ni ko'radi. Backend: `authorize.PERSONNEL`, davomat — INTERNAL/FINANCE.
  ACCOUNTANT: ['dashboard', 'students', 'teachers', 'attendance', 'payments', 'reports', 'expenses', 'payroll', 'staff', 'faceid', 'admissions', 'contracts', 'announcements', 'settings'],
  RECEPTION: ['dashboard', 'students', 'admissions', 'contracts', 'announcements', 'settings'],
  TEACHER: ['dashboard', 'students', 'schedule', 'attendance', 'grades', 'journal', 'homework', 'ranking', 'behavior', 'exams', 'announcements', 'settings'],
  HOMEROOM_TEACHER: ['dashboard', 'students', 'classes', 'schedule', 'attendance', 'grades', 'journal', 'homework', 'ranking', 'behavior', 'exams', 'announcements', 'settings'],
  PARENT: [],
  STUDENT: [],
};

/** Bu panelга kirishi mumkin bo'lgan ichki rollar (PARENT/STUDENT emas). */
export const INTERNAL_ROLES = [
  'SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION', 'TEACHER', 'HOMEROOM_TEACHER',
];

export function isInternalRole(role?: string | null): boolean {
  return !!role && INTERNAL_ROLES.includes(role);
}

/** Platforma superadmini — SaaS egasi. Migratsiyadan so'ng yagona SUPER_ADMIN shu. */
export function isPlatformSuperadmin(user?: { role?: string; tenantId?: string | null } | null): boolean {
  return user?.role === 'SUPER_ADMIN';
}

/** Rol darajasi — backenddagi `middleware/authorize.ROLE_RANK` bilan BIR XIL bo'lishi shart. */
const ROLE_RANK: Record<string, number> = {
  SUPER_ADMIN: 100, DIRECTOR: 80, ADMIN: 60, ACCOUNTANT: 40, RECEPTION: 40,
  TEACHER: 20, HOMEROOM_TEACHER: 20, PARENT: 0, STUDENT: 0,
};
const roleRank = (r?: string | null): number => (r ? ROLE_RANK[r] ?? 0 : 0);

/**
 * `actor` shu rolni tayinlay oladimi va shu rolli xodimni tahrirlay/o'chira oladimi —
 * backend `canAssignRole` / `canManageUserOfRole` ning nusxasi (o'zidan yuqorini emas).
 * UI'da ortiqcha tugma/variant ko'rsatmaslik uchun; haqiqiy tekshiruv serverda.
 */
export function canManageRole(actor?: string | null, target?: string | null): boolean {
  return roleRank(target) <= roleRank(actor);
}

/** Boshqaruv rollari — backenddagi `middleware/authorize.STAFF` guruhi bilan bir xil. */
export const STAFF_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN'];

/**
 * Maktab darajasidagi STAFF — tenant sozlamalarini (kiosk havolasi, shartnoma
 * rekvizitlari, support email) boshqara oladi. ACCOUNTANT/RECEPTION backendda 403
 * oladi. Platforma superadmini (tenantId=null) KIRMAYDI: uning maktab konteksti
 * yo'q — bu endpointlar unga doim 400 qaytaradi.
 */
export function isTenantStaff(user?: { role?: string; tenantId?: string | null } | null): boolean {
  return !!user?.role && STAFF_ROLES.includes(user.role) && !isPlatformSuperadmin(user);
}

/**
 * Maktab direktori — panel qurilmasini tasdiqlash / rad etish (spec 2026-10-15 §8.5).
 * Backend: `routes/qurilma.ts` → DIRECTOR (+ maktab ichidagi SUPER_ADMIN, panelda amalda yo'q).
 */
export function isTenantDirector(user?: { role?: string } | null): boolean {
  return user?.role === 'DIRECTOR';
}

/**
 * Premium bilan bog'liq bo'limlar (muzlatish bayrog'i uchun yagona ro'yxat).
 * `lib/features.ts` → `PREMIUM_UI_ENABLED = false` bo'lganda bu bo'limlar yon
 * menyudan va Spotlight qidiruvidan olib tashlanadi (`lib/nav.ts`).
 *
 * ATAYLAB `ROLE_SECTIONS` dan olib tashlanmadi: marshrutlar ochiq qoladi va
 * to'g'ridan-to'g'ri URL bilan kirgan admin chalg'ituvchi "Ruxsat yo'q" (Guard)
 * o'rniga sahifaning o'zidagi "vaqtincha o'chirilgan" xabarini ko'radi
 * (`components/FeatureFrozen.tsx`). Bayroq qaytarilganda bu ro'yxat faqat
 * hujjat sifatida qolaveradi — hech narsani cheklamaydi.
 */
export const PREMIUM_SECTIONS: Section[] = ['premium', 'saas-premium', 'saas-gifts'];

export function allowedSections(role?: string | null): Section[] {
  return (role && ROLE_SECTIONS[role]) || [];
}

export function canAccess(role: string | null | undefined, section: Section): boolean {
  return allowedSections(role).includes(section);
}
